import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "./Header.css";
import { api } from "../../api";
import NavDropdown, { MobileNavGroup } from "./NavDropdown";

const logo = process.env.PUBLIC_URL + "/logo.png";

const YT_CHANNEL = "https://youtube.com/@freesekhoo?si=PVuvM3YFPzpYiKbZ";

const APP_VERSION = "2.1";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [headline, setHeadline] = useState('');
  const [headlines, setHeadlines] = useState([]);

  const navItems = [
    { key: "mcqs", label: "Imp MCQs", path: "/select-subject/mcqs" },
    { key: "notes", label: "PDF Notes", path: "/select-subject/notes/pdf-notes" },
    { key: "quiz", label: "Mock Exam", path: "/quiz" },
    { key: "past-papers", label: "Past Papers", path: "/past-papers" },
    { key: "lectures", label: "Video Lectures", path: YT_CHANNEL },
    { key: "courses", label: "Courses", path: "/courses" },
  ];
  const [currentUser, setCurrentUser] = useState(null);
  const location = useLocation();
  const [subjectMenus, setSubjectMenus] = useState({ mcq: [], handwritten: [], pastpaper: [] });
  const [openMobileGroup, setOpenMobileGroup] = useState(null);

  const menuLinkFor = (kind, name) => {
    const n = encodeURIComponent(name);
    if (kind === 'mcq') return `/mcqs/subject/${n}`;
    if (kind === 'handwritten') return `/notes/pdf-notes/subject/${n}`;
    return `/past-papers/subject/${n}`;
  };

  const isStudent = currentUser && currentUser.role === 'student';

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('authToken') || ''
      await fetch(api('/api/auth/logout'), { method: 'POST', credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : undefined })
    } catch (_) {}
    localStorage.removeItem('authToken')
    setCurrentUser(null)
    window.location.href = '/'
  };

  useEffect(() => {
    const loadHeadlines = async () => {
      try {
        let all = []
        let items = null
        let activeConfigured = false
        const r = await fetch(api('/api/site/headlines'))
        if (r.ok) {
          const d = await r.json()
          all = Array.isArray(d.headlines) ? d.headlines : []
          if (Array.isArray(d.items)) { items = d.items; activeConfigured = !!d.activeConfigured }
        }

        let useArr = all
        // `explicitlyEmpty` = the admin has a selection and it turns everything
        // off. In that case show the built-in default, not the legacy single line.
        let explicitlyEmpty = false

        if (items) {
          if (activeConfigured) {
            useArr = items.filter((x) => x && x.active).map((x) => x.text)
            explicitlyEmpty = useArr.length === 0
          } else {
            useArr = all
          }
        } else {
          // Older API shape — fall back to the separate active-index endpoint.
          try {
            const r2 = await fetch(api('/api/site/headlines-active'))
            if (r2.ok) {
              const d2 = await r2.json()
              const act = Array.isArray(d2.active) ? d2.active : []
              if (d2.configured) {
                useArr = act.map((i) => all[i]).filter((x) => typeof x === 'string')
                explicitlyEmpty = useArr.length === 0
              } else if (act.length > 0) {
                useArr = act.map((i) => all[i]).filter((x) => typeof x === 'string')
              }
            }
          } catch (_) {}
        }

        setHeadlines(useArr)
        if (useArr.length === 0 && !explicitlyEmpty) {
          const r3 = await fetch(api('/api/site/headline'))
          if (r3.ok) { const d3 = await r3.json(); setHeadline(d3.headline || '') }
        }
      } catch (_) {}
    }
    loadHeadlines()
  }, [])

  useEffect(() => {
    // Show a cached copy instantly, then refresh in the background — once per load,
    // not per navigation.
    try {
      const cached = sessionStorage.getItem('fs_subject_menus');
      if (cached) setSubjectMenus(JSON.parse(cached));
    } catch (_) {}

    const loadSubjectMenus = async () => {
      try {
        const kinds = ['mcq', 'handwritten', 'pastpaper'];
        const results = await Promise.all(
          kinds.map(async (k) => {
            try {
              const r = await fetch(api(`/api/mcqs/subjects?type=${k}`));
              if (!r.ok) return [];
              const d = await r.json();
              return Array.isArray(d.subjects) ? d.subjects : [];
            } catch (_) {
              return [];
            }
          })
        );
        const next = { mcq: results[0], handwritten: results[1], pastpaper: results[2] };
        setSubjectMenus(next);
        try { sessionStorage.setItem('fs_subject_menus', JSON.stringify(next)); } catch (_) {}
      } catch (_) {}
    };
    loadSubjectMenus();
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const token = localStorage.getItem('authToken') || ''
        const r = await fetch(api("/api/auth/me"), {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (r.ok) {
          const d = await r.json();
          setCurrentUser(d.user || null);
        } else {
          setCurrentUser(null);
        }
      } catch (_) {
        setCurrentUser(null);
      }
    };
    loadUser();
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setOpenMobileGroup(null);
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {}
  }, [location.pathname]);

  return (
    <header className="w-full sticky top-0 z-50">
      {/* Scrolling headline */}
      <div className="gradient-bg text-white py-1 overflow-hidden">
        <div className="scrolling-headline-container">
          {(() => {
            const items = (Array.isArray(headlines) && headlines.length > 0 ? headlines : [headline])
              .filter(Boolean);
            const text = (items.length > 0 ? items : ['Welcome to FreeSeekho - Your One-Stop Learning Platform!'])
              .join('   •   ');
            return (
              <div className="headline-marquee" aria-label={text}>
                <span className="headline-marquee__item font-medium text-sm md:text-base">{text}</span>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Main navigation */}
      <nav className="bg-white shadow-md py-2">
        <div className="container mx-auto px-6 md:px-10 flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="mr-4 inline-flex flex-col items-end leading-none">
            <img src={logo} alt="FreeSeekho Logo" className="h-8" />
            <span className="mt-0.5 text-[9px] font-medium uppercase tracking-[0.2em] text-gray-400">
              v{APP_VERSION}
            </span>
          </Link>

          {/* Mobile menu button */}
          <button
            className="lg:hidden focus:outline-none"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              {isMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>

          <div className="hidden lg:flex items-center space-x-3 xl:space-x-5 text-sm xl:text-base">
            <Link to="/" className="nav-link nav-red">
              <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7A1 1 0 003 10h1v6a1 1 0 001 1h3a1 1 0 001-1v-3h2v3a1 1 0 001 1h3a1 1 0 001-1v-6h1a1 1 0 00.707-1.707l-7-7z"/></svg>
              Home
            </Link>
            <NavDropdown
              to="/select-subject/mcqs"
              label="Imp MCQs"
              colorClass="nav-orange"
              icon={<svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M10 2a1 1 0 01.894.553l1.618 3.236 3.57.519a1 1 0 01.554 1.706l-2.584 2.52.61 3.557a1 1 0 01-1.45 1.054L10 13.347l-3.212 1.694a1 1 0 01-1.45-1.054l.61-3.557-2.584-2.52a1 1 0 01.554-1.706l3.57-.519L9.106 2.553A1 1 0 0110 2z"/></svg>}
              items={subjectMenus.mcq}
              hrefFor={(s) => menuLinkFor('mcq', s.name)}
            />
            {navItems
              .filter((i) => i.key !== "mcqs")
              .map((item) =>
                item.key === "quiz" ? (
                  <Link key={item.key} to={item.path} className="nav-link nav-purple">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" /><path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                    {item.label}
                  </Link>
                ) : item.key === "past-papers" ? (
                  <NavDropdown
                    key={item.key}
                    to={item.path}
                    label={item.label}
                    colorClass="nav-orange"
                    icon={<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" /></svg>}
                    items={subjectMenus.pastpaper}
                    hrefFor={(s) => menuLinkFor('pastpaper', s.name)}
                  />
                ) : item.key === "notes" ? (
                  <NavDropdown
                    key={item.key}
                    to={item.path}
                    label={item.label}
                    colorClass="nav-green"
                    icon={<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" /><path fillRule="evenodd" d="M8 4a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1zm-3 5a1 1 0 011-1h10a1 1 0 110 2H6a1 1 0 01-1-1zm0 3a1 1 0 011-1h10a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" /></svg>}
                    items={subjectMenus.handwritten}
                    hrefFor={(s) => menuLinkFor('handwritten', s.name)}
                  />
                ) : (
                  item.key === "lectures" ? (
                    <a key={item.key} href={item.path} target="_blank" rel="noopener noreferrer" className="nav-link nav-blue">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" /></svg>
                      {item.label}
                    </a>
                  ) : (
                    <Link key={item.key} to={item.path} className="nav-link nav-teal">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M3 8a1 1 0 011-1h12a1 1 0 011 1v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm3-5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"/></svg>
                      {item.label}
                    </Link>
                  )
                )
              )}
            {currentUser && currentUser.isAdmin && (
              <Link to="/qasimmian4655547" className="nav-link nav-red">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M10 2a2 2 0 012 2v1h2a2 2 0 012 2v7a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2h2V4a2 2 0 012-2zm0 6a1 1 0 100 2h4a1 1 0 100-2h-4z" clipRule="evenodd"/></svg>
                Admin
              </Link>
            )}
            {isStudent && (
              <>
                <Link to="/my-courses" className="nav-link nav-green">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3z"/></svg>
                  My Courses
                </Link>
                <button onClick={handleLogout} className="nav-link nav-red">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h6a1 1 0 100-2H4V5h5a1 1 0 000-2H3zm10.293 3.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L14.586 11H7a1 1 0 110-2h7.586l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
                  Logout
                </button>
              </>
            )}
          </div>
        </div>

        {/* Mobile Navigation */}
        <div
          className={`lg:hidden grid transition-all duration-300 ease-in-out ${
            isMenuOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
          }`}
        >
          <div className="overflow-hidden">
            <div className="flex flex-col px-4 py-6 space-y-3">
              <MobileNavGroup
                to="/select-subject/mcqs"
                label="Imp MCQs"
                colorClass="nav-orange"
                icon={<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M10 2a1 1 0 01.894.553l1.618 3.236 3.57.519a1 1 0 01.554 1.706l-2.584 2.52.61 3.557a1 1 0 01-1.45 1.054L10 13.347l-3.212 1.694a1 1 0 01-1.45-1.054l.61-3.557-2.584-2.52a1 1 0 01.554-1.706l3.57-.519L9.106 2.553A1 1 0 0110 2z" /></svg>}
                items={subjectMenus.mcq}
                hrefFor={(s) => menuLinkFor('mcq', s.name)}
                open={openMobileGroup === 'mcq'}
                onToggle={() => setOpenMobileGroup((g) => (g === 'mcq' ? null : 'mcq'))}
              />
              <MobileNavGroup
                to="/select-subject/notes/pdf-notes"
                label="PDF Notes"
                colorClass="nav-green"
                icon={<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" /><path fillRule="evenodd" d="M8 4a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1zm-3 5a1 1 0 011-1h10a1 1 0 110 2H6a1 1 0 01-1-1zm0 3a1 1 0 011-1h10a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" /></svg>}
                items={subjectMenus.handwritten}
                hrefFor={(s) => menuLinkFor('handwritten', s.name)}
                open={openMobileGroup === 'handwritten'}
                onToggle={() => setOpenMobileGroup((g) => (g === 'handwritten' ? null : 'handwritten'))}
              />
              <Link to="/quiz" className="mobile-nav-link nav-purple flex items-center gap-3">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" /><path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                Mock Exam
              </Link>
              <MobileNavGroup
                to="/past-papers"
                label="Past Papers"
                colorClass="nav-orange"
                icon={<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" /></svg>}
                items={subjectMenus.pastpaper}
                hrefFor={(s) => menuLinkFor('pastpaper', s.name)}
                open={openMobileGroup === 'pastpaper'}
                onToggle={() => setOpenMobileGroup((g) => (g === 'pastpaper' ? null : 'pastpaper'))}
              />
              <a href={YT_CHANNEL} target="_blank" rel="noopener noreferrer" className="mobile-nav-link nav-blue flex items-center gap-3" onClick={() => setIsMenuOpen(false)}>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" /></svg>
                Video Lectures
              </a>
              <Link to="/courses" className="mobile-nav-link nav-green flex items-center gap-3">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3z" /></svg>
                Courses
              </Link>
              {isStudent && (
                <>
                  <Link to="/my-courses" className="mobile-nav-link nav-green flex items-center gap-3">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M11.3 1.046a1 1 0 00-1.3.954v3.5a1 1 0 001 1h3.5a1 1 0 00.954-1.3A6.003 6.003 0 0011.3 1.046zM10.5 7.5a1 1 0 01-1-1V3a1 1 0 00-1-1 7 7 0 107.868 8.868 1 1 0 00-1.302-.868H10.5z" clipRule="evenodd" /></svg>
                    My Courses
                  </Link>
                  <button onClick={() => { setIsMenuOpen(false); handleLogout(); }} className="mobile-nav-link nav-red flex items-center gap-3 text-left w-full">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h6a1 1 0 100-2H4V5h5a1 1 0 000-2H3zm10.293 3.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L14.586 11H7a1 1 0 110-2h7.586l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                    Logout
                  </button>
                </>
              )}
              {currentUser && currentUser.isAdmin && (
                <Link to="/qasimmian4655547" className="mobile-nav-link nav-red flex items-center gap-3">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M10 2a2 2 0 012 2v1h2a2 2 0 012 2v7a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2h2V4a2 2 0 012-2zm0 6a1 1 0 100 2h4a1 1 0 100-2h-4z" clipRule="evenodd" /></svg>
                  Admin
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
